import { getProjectTeamStatus, getTeamStatusDisplay } from '@/utils/status';
import supabase from '@/utils/supabase';
import { useLoaderData, useFetcher } from 'react-router';
import { getColumns } from './components/dashboard/columns';
import { DataTable } from './components/dashboard/data-table';
import type { ProjectRole } from '@/utils/status';

export async function loader() {
  const { data: projects, error: projectsError } = await supabase
    .from('projects')
    .select(
      'id, project_name, status, due_date, clients(company_name), deliverables(id, deliverables_team(team_status)), project_members(user_id, project_role, profile(display_name))'
    )
    .neq('status', 'cancelled')
    .neq('status', 'approved')
    .order('created_at', { ascending: false });

  if (projectsError || !projects) {
    throw new Response('Failed to load projects', { status: 500 });
  }

  const { data: teamUsers, error: teamUsersError } = await supabase
    .from('profile')
    .select('id, display_name')
    .in('role', ['team', 'owner']);

  if (teamUsersError || !teamUsers) {
    throw new Response('Failed to load team users', { status: 500 });
  }

  const rows = projects.map((project) => {
    const teamStatuses = project.deliverables
      .map((d) => d.deliverables_team?.team_status)
      .filter((s): s is string => Boolean(s));
    const statusCode = getProjectTeamStatus(teamStatuses, 'producer');
    const { label, colour } = getTeamStatusDisplay(statusCode, 'producer');

    const members = project.project_members ?? [];

    return {
      id: project.id,
      name: project.project_name,
      client_name: project.clients?.company_name,
      status: { label, colour },
      due_date: project.due_date,
      deliverable_count: project.deliverables.length,
      producers: members
        .filter((m) => m.project_role === 'producer')
        .map((m) => ({ id: m.user_id, name: m.profile?.display_name ?? '' })),
      editors: members
        .filter((m) => m.project_role === 'editor')
        .map((m) => ({ id: m.user_id, name: m.profile?.display_name ?? '' })),
      reviewers: members
        .filter((m) => m.project_role === 'reviewer')
        .map((m) => ({ id: m.user_id, name: m.profile?.display_name ?? '' })),
    };
  });

  return {
    rows,
    teamUsers: teamUsers.map((u) => ({ id: u.id, name: u.display_name ?? '' })),
  };
}

export async function action({ request }: { request: Request }) {
  const formData = await request.formData();
  const intent = formData.get('intent');
  const projectId = formData.get('projectId') as string;
  const userId = formData.get('userId') as string;
  const role = formData.get('role') as ProjectRole;

  if (intent === 'assign') {
    const { error } = await supabase
      .from('project_members')
      .insert({ project_id: projectId, user_id: userId, project_role: role });
    if (error) throw new Response('Failed to assign', { status: 500 });
  }
  if (intent === 'unassign') {
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .eq('project_role', role);
    if (error) throw new Response('Failed to unassign', { status: 500 });
  }
  return null;
}

export default function EngineIndex() {
  const { rows, teamUsers } = useLoaderData();
  const fetcher = useFetcher();

  function handleAssign(projectId: string, userId: string, role: string) {
    fetcher.submit(
      { intent: 'assign', projectId, userId, role },
      { method: 'POST' }
    );
  }

  function handleUnassign(projectId: string, userId: string, role: string) {
    fetcher.submit(
      { intent: 'unassign', projectId, userId, role },
      { method: 'POST' }
    );
  }

  const columns = getColumns(teamUsers, handleAssign, handleUnassign);

  return (
    <div className="flex flex-col px-4">
      <DataTable columns={columns} data={rows} />
    </div>
  );
}
